import React, { useMemo, useState } from 'react';
import axios from 'axios';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import { getToday, isValidRange } from 'pages/stats/statsUtils';

const DOCX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const getMonthRange = (month) => {
  const match = String(month || '').match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  if (monthNumber < 1 || monthNumber > 12) return null;

  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return {
    from: `${month}-01`,
    to: `${month}-${String(lastDay).padStart(2, '0')}`
  };
};

const getInitialFilters = () => ({
  mode: 'month',
  month: getToday().slice(0, 7),
  from: '',
  to: ''
});

const filenameFromHeader = (header, fallback) => {
  const match = String(header || '').match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const getDownloadError = async (requestError) => {
  const responseData = requestError.response?.data;
  if (responseData instanceof Blob) {
    try {
      const message = JSON.parse(await responseData.text())?.message;
      if (message) return message;
    } catch (error) {
      // Fall back to the translated generic message below.
    }
  }
  return 'Справката не можа да бъде създадена. Моля, опитайте отново.';
};

export default function Reports() {
  const initialFilters = useMemo(getInitialFilters, []);
  const [filters, setFilters] = useState(initialFilters);
  const [reportType, setReportType] = useState('orders');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  const selectedRange =
    filters.mode === 'month'
      ? getMonthRange(filters.month)
      : { from: filters.from, to: filters.to };
  const isRangeValid = Boolean(selectedRange && isValidRange(selectedRange));

  const updateFilter = (field) => (event) => {
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  const downloadReport = async (event) => {
    event.preventDefault();
    if (!isRangeValid || !selectedRange) return;

    setError('');
    setIsDownloading(true);
    try {
      const response = await axios.get(`/reports/${reportType}`, {
        params: selectedRange,
        responseType: 'blob'
      });
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: DOCX_CONTENT_TYPE });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = filenameFromHeader(
        response.headers?.['content-disposition'],
        `${reportType === 'articles' ? 'article' : 'order'}-report_${selectedRange.from}_${selectedRange.to}.docx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (requestError) {
      setError(await getDownloadError(requestError));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <VendorDashboardLayout maxWidth="lg">
      <DashboardPageHeader
        title="Справки"
        subtitle="Създавайте Word справки по поръчки или артикули за избран месец или персонален период."
        icon={DescriptionRoundedIcon}
      />

      <Tabs
        value={reportType}
        onChange={(_, value) => { setReportType(value); setError(''); }}
        aria-label="Вид справка"
        sx={{ mb: 2.5 }}
      >
        <Tab value="orders" label="По поръчки" disabled={isDownloading} />
        <Tab value="articles" label="По артикули" disabled={isDownloading} />
      </Tabs>

      <Card component="form" onSubmit={downloadReport}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <CalendarMonthRoundedIcon color="primary" />
            <Typography variant="h6">Период на справката</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {reportType === 'articles'
              ? 'Изделията са групирани по вид плат, артикул и размер. Ранфорс (R-), сатен (S-) и сатен 300 ТС (S-…300 TC) се разпознават по името или кода на продукта. В колоната „Брой“ се вижда всяко отделно поръчано количество и сборът му. Документът включва общи суми по артикули и платове. Периодът е по дата на поръчката.'
              : 'Документът съдържа номер и дата на поръчката, стойност на продуктите, доставка, обща сума и начин на плащане. Сумите са в евро.'}
          </Typography>

          <Grid container spacing={2.25} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="report-mode-label">Вид период</InputLabel>
                <Select
                  labelId="report-mode-label"
                  label="Вид период"
                  value={filters.mode}
                  onChange={updateFilter('mode')}
                >
                  <MenuItem value="month">Месечна справка</MenuItem>
                  <MenuItem value="custom">Избран период</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {filters.mode === 'month' ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="month"
                  label="Месец"
                  value={filters.month}
                  onChange={updateFilter('month')}
                  InputLabelProps={{ shrink: true }}
                  error={!isRangeValid}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="От дата"
                    value={filters.from}
                    onChange={updateFilter('from')}
                    InputLabelProps={{ shrink: true }}
                    error={!isRangeValid}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="До дата"
                    value={filters.to}
                    onChange={updateFilter('to')}
                    InputLabelProps={{ shrink: true }}
                    error={!isRangeValid}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                startIcon={<DownloadRoundedIcon />}
                disabled={!isRangeValid || isDownloading}
              >
                {isDownloading ? 'Създаване...' : 'Изтегли .docx'}
              </Button>
            </Grid>
          </Grid>

          {!isRangeValid && (
            <Typography color="error" variant="caption" display="block" sx={{ mt: 1.5 }}>
              Изберете валиден период, като началната дата е преди крайната.
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}

      {reportType === 'orders' && <Box sx={{ mt: 2.5, px: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Заглавието на месечния документ е „ОПИС-СПРАВКА ЗА ПОРЪЧКИТЕ – ММ-ГГГГ –
          Мина Актуал Про ЕООД“.
        </Typography>
      </Box>}
    </VendorDashboardLayout>
  );
}
